import {
  GoabAppFooter,
  GoabAppFooterMetaSection,
  GoabAppHeader,
  GoabAppHeaderMenu,
  GoabCircularProgress,
  GoabMicrositeHeader,
  GoabNotification,
} from '@abgov/angular-components';
import { EnvironmentHeaderComponent } from '@ala/common-ui';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { KeycloakService } from '@libs/security/services/keycloak/keycloak.service';
import {
  AppConfig,
  ConfigInitService,
} from '@libs/services/config-init.service';
import { ProgressIndicatorService } from '@libs/services/progress-indicator.service';
import { UserService } from '@libs/services/user/user.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { PersonalNamePipe } from './shared/pipes/personal-name.pipe';

@UntilDestroy()
@Component({
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    RouterModule,
    RouterOutlet,
    EnvironmentHeaderComponent,
    GoabAppHeader,
    GoabAppHeaderMenu,
    GoabMicrositeHeader,
    GoabNotification,
    GoabAppFooter,
    GoabAppFooterMetaSection,
    RouterOutlet,
    GoabCircularProgress,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  progressIndicatorService = inject(ProgressIndicatorService);
  router = inject(Router);
  logout = output<Event>();

  private configService = inject(ConfigInitService);
  private keycloakService = inject(KeycloakService);
  private metaService = inject(Meta);
  private userService = inject(UserService);

  private config = signal<AppConfig | undefined>(undefined);
  environmentName = computed(
    () => this.config()?.ENVIRONMENT_NAME ?? 'environment name unavailable'
  );
  applicationVersion = computed(() => this.config()?.APP_VERSION);
  isProd = signal<boolean>(false);
  echoIsProd = effect(() => {
    // isProd changed logging removed to prevent flaky test detection
  });
  environmentHeaderDismissed = signal<boolean>(false);
  userName = signal<string | null>(null);
  isLoadingUser = signal<boolean>(false);

  private currentUrl = signal<string>('');
  // private helpBannerDismissed = signal<boolean>(false);
  private urlEffect = effect(() => {
    if (this.currentUrl() === '/help') {
      sessionStorage.setItem('helpBannerDismissed', 'true');
    }
  });

  doShowHelpBanner = computed(() => {
    return (
      this.currentUrl() !== '/help' &&
      !sessionStorage.getItem('helpBannerDismissed')
    );
  });

  async ngOnInit(): Promise<void> {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        this.currentUrl.set(event.url);
      });

    this.config.set(this.configService.getConfig());

    this.isProd.set(
      this.environmentName().toLowerCase().includes('prod') ?? false
    );

    this.metaService.addTags([
      {
        name: 'environment',
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        content: this.environmentName()!,
      },
      {
        name: 'application-version',
        content: this.applicationVersion() || 'unknown',
      },
    ]);

    // Load current user
    await this.loadCurrentUser();
  }

  private async loadCurrentUser(): Promise<void> {
    this.isLoadingUser.set(true);

    this.userService
      .getCurrentUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          if (result?.user) {
            this.userName.set(
              new PersonalNamePipe().transform(
                result.user.firstName,
                result.user.lastName
              )
            );
          }
          if (!this.userName()) {
            this.userName.set('Unknown User');
          }
          this.isLoadingUser.set(false);
        },
        error: (err) => {
          this.isLoadingUser.set(false);
          if (err.message === 'Unauthorized') {
            this.router.navigate(['unauthorized']);
          }
          console.error('Failed to load current user:', err);
          this.userName.set(null);
        },
      });
  }

  onEnvironmentHeaderDismissed() {
    this.environmentHeaderDismissed.set(true);
  }

  onLogout(event: Event) {
    this.progressIndicatorService.isProgressIndicator.set(true);
    this.progressIndicatorService.progressIndicatorMessage.set(
      'Signing you out...'
    );
    event.preventDefault();
    this.keycloakService.logout().subscribe({
      next: () => {
        this.progressIndicatorService.isProgressIndicator.set(false);
        this.progressIndicatorService.progressIndicatorMessage.set('');
        // Clear user data on logout
        this.userName.set(null);
        this.clearSession();
      },
      error: (error) => {
        this.progressIndicatorService.isProgressIndicator.set(false);
        this.progressIndicatorService.progressIndicatorMessage.set('');
        console.error('Logout failed:', error);
        // Clear user data even on error
        this.userName.set(null);
      },
    });
  }

  notificationDismissed() {
    // this.helpBannerDismissed.set(true);
    sessionStorage.setItem('helpBannerDismissed', 'true');
  }

  clearSession() {
    sessionStorage.clear();
  }
}
